import { useState } from 'react';
import Modal from '../modal/modal';
import Card from '../card/Card';
import Input from '../input/Input';
import DropDown from '../dropdown/DropDown';
import P from '../texts/p/P';
import { toast } from 'react-toastify';
import { createFund } from '../../services/GuaranteesService';

interface NewFundModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const NewFundModal = ({
  isOpen,
  onClose,
  onSuccess,
}: NewFundModalProps) => {
  const [isLoading, setIsLoading] = useState(false);

  // Form values
  const [name, setName] = useState<string>('');
  const [isTaxDeductible, setIsTaxDeductible] = useState<boolean | null>(null);

  const resetForm = () => {
    setName('');
    setIsTaxDeductible(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    // Validations
    if (!name || name.trim() === '') {
      toast.error('Ingrese el nombre del fondo');
      return;
    }

    if (isTaxDeductible === null) {
      toast.error('Seleccione si es desgravable');
      return;
    }

    setIsLoading(true);
    try {
      const requestBody = {
        name: name.trim(),
        is_tax_deductible: isTaxDeductible,
      };

      const response = await createFund(requestBody);
      
      if (response.success) {
        toast.success('Fondo creado exitosamente');
        resetForm();
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        const errorMsg = 'message' in response ? response.message : 'Error al crear fondo';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error creating fund:', error);
      toast.error('Error al crear fondo');
    } finally {
      setIsLoading(false);
    }
  };

  // Dropdown helpers for Es desgravable
  const taxDeductibleItems = [
    { label: 'SI', disabled: false },
    { label: 'NO', disabled: false }
  ];

  const getSelectedTaxDeductibleName = () => {
    if (isTaxDeductible === null) return 'Seleccionar';
    return isTaxDeductible ? 'SI' : 'NO';
  };

  const handleTaxDeductibleChange = (selectedLabel: string) => {
    setIsTaxDeductible(selectedLabel === 'SI');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Añadir nuevo fondo"
      width='600px'
      primaryText='Agregar'
      primaryVariant='primary'
      onPrimaryAction={handleSubmit}
      primaryActionDisabled={isLoading}
      secondaryText='Cancelar'
      secondaryVariant='contained'
      onSecondaryAction={handleClose}
      showOverlay={true}
    >
      <Card margin={{ top: '4' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          {/* Nombre */}
          <Input
            label="Nombre *"
            name="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isLoading}
            size="md"
          />

          {/* Es desgravable */}
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Es desgravable *
            </P>
            <Card margin={{ top: '2' }}>
              <DropDown
                id="is_tax_deductible"
                title={getSelectedTaxDeductibleName()}
                items={taxDeductibleItems}
                onChange={handleTaxDeductibleChange}
                size="md"
              />
            </Card>
          </Card>
        </div>
      </Card>
    </Modal>
  );
};

export default NewFundModal;
